import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'StoryMeal';
const SITE_URL = 'https://storymeal.com';
const DEFAULT_IMAGE = `${SITE_URL}/StoryMealLogo2.png`;
const DEFAULT_DESCRIPTION = 'StoryMeal is a free online story platform where readers and writers meet. Discover thousands of stories across genres including Fantasy, Romance, Mystery, Horror, Science Fiction, Folk Tales, Fables, Poetry, and more.';

/**
 * Reusable SEO component for managing meta tags, Open Graph, Twitter Cards,
 * canonical URLs, and JSON-LD structured data.
 *
 * @param {Object} props
 * @param {string} props.title - Page title (will be appended with site name)
 * @param {string} props.description - Meta description
 * @param {string} props.path - URL path (e.g., '/story/123') for canonical/og:url
 * @param {string} props.image - OG image URL (defaults to site logo)
 * @param {string} props.type - OG type ('website', 'article', 'profile')
 * @param {Object} props.schema - JSON-LD structured data object
 * @param {boolean} props.noindex - Whether to set noindex robots meta
 * @param {string} props.keywords - Additional keywords
 */
function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  schema,
  noindex = false,
  keywords,
  author,
  publishedTime,
  modifiedTime,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Read, Write, and Share Stories Online | Free Story Platform`;
  const fullUrl = `${SITE_URL}${path}`;
  const fullImage = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title || SITE_NAME} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {author && <meta property="article:author" content={author} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title || SITE_NAME} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:image:alt" content={title || SITE_NAME} />

      {/* JSON-LD Structured Data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}

export default SEO;
export { SITE_NAME, SITE_URL, DEFAULT_IMAGE, DEFAULT_DESCRIPTION };